/**
 * Import Modal Component - Add YouTube URLs for download
 */
import React, { useState } from 'react';
import { songsAPI } from '../services/api';
import './ImportModal.css';

const ImportModal = ({ onClose, onImportSuccess }) => {
  const [urls, setUrls] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState(null);
  const [fileName, setFileName] = useState(null);
  const fileInputRef = React.createRef();

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Parse URLs (split by newlines, commas, or spaces)
    const urlList = urls
      .split(/[\n,\s]+/)
      .map(url => url.trim())
      .filter(url => url.length > 0);

    if (urlList.length === 0) {
      setError('Please enter at least one YouTube URL');
      return;
    }

    // Validate URLs - support multiple formats:
    // - Full YouTube URLs (youtube.com, youtu.be)
    // - "youtube [video_id]" format
    // - Plain 11-character video IDs
    const isValidYouTubeInput = (url) => {
      // Full YouTube URLs
      if (url.includes('youtube.com') || url.includes('youtu.be')) {
        return true;
      }

      // "youtube [video_id]" format
      if (/^youtube\s+[a-zA-Z0-9_-]+$/i.test(url)) {
        return true;
      }

      // Plain 11-character video ID (standard YouTube format)
      if (/^[a-zA-Z0-9_-]{11}$/.test(url)) {
        return true;
      }

      return false;
    };

    const invalidUrls = urlList.filter(url => !isValidYouTubeInput(url));

    if (invalidUrls.length > 0) {
      setError(`Invalid YouTube URLs: ${invalidUrls.slice(0, 3).join(', ')}${invalidUrls.length > 3 ? '...' : ''}`);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await songsAPI.importSongs(urlList);
      setResults(response.data.results);
      setSummary(response.data.summary);

      // Notify parent component of successful import
      if (onImportSuccess) {
        onImportSuccess(response.data.results);
      }

      // Clear input after successful import
      setUrls('');

      // Auto-close after 3 seconds if all imports were successful
      const allSuccessful = response.data.results.every(r => r.status !== 'failed');
      if (allSuccessful) {
        setTimeout(() => {
          onClose();
        }, 3000);
      }

    } catch (err) {
      console.error('Import failed:', err);
      setError(err.response?.data?.error || 'Failed to import songs');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File too large. Maximum size is 5MB.');
      return;
    }

    // Check file type
    if (!file.name.match(/\.(txt|csv|json)$/i)) {
      setError('Invalid file type. Please upload .txt, .csv, or .json files.');
      return;
    }

    setFileName(file.name);
    setError(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      setUrls(content);
    };
    reader.onerror = () => {
      setError('Failed to read file. Please try again.');
      setFileName(null);
    };
    reader.readAsText(file);
  };

  const handleClearFile = () => {
    setFileName(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} onKeyDown={handleKeyDown}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Import YouTube URLs</h2>
          <button className="btn-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="file-upload" className="file-upload-label">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
                </svg>
                {fileName ? `📄 ${fileName}` : '📁 Upload file with URLs (.txt, .csv, .json)'}
              </label>
              <input
                id="file-upload"
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".txt,.csv,.json"
                disabled={loading}
                className="file-input"
              />
              {fileName && (
                <button
                  type="button"
                  onClick={handleClearFile}
                  className="btn-clear-file"
                  disabled={loading}
                >
                  Clear file
                </button>
              )}
              <div className="input-divider">
                <span>or paste URLs directly</span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="urls">YouTube URLs (one per line, separated by commas or spaces):</label>
              <textarea
                id="urls"
                value={urls}
                onChange={(e) => setUrls(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=...&#10;youtube Ig86MxQ7p4Y&#10;Ig86MxQ7p4Y"
                rows={8}
                disabled={loading}
                autoFocus
              />
              <small>Enter up to 50 items. Supports: Full URLs, "youtube [video_id]", or plain video IDs</small>
            </div>

            {error && (
              <div className="alert alert-error">
                {error}
              </div>
            )}

            {summary && (
              <div className="import-summary">
                <h3>Import Summary:</h3>
                <div className="summary-stats">
                  <div className="summary-item total">
                    <span className="summary-label">Total Processed:</span>
                    <span className="summary-value">{summary.total_processed}</span>
                  </div>
                  <div className="summary-item added">
                    <span className="summary-label">✓ Added:</span>
                    <span className="summary-value">{summary.added}</span>
                  </div>
                  <div className="summary-item skipped">
                    <span className="summary-label">⊘ Skipped:</span>
                    <span className="summary-value">{summary.skipped}</span>
                  </div>
                  <div className="summary-item retried">
                    <span className="summary-label">↻ Retried:</span>
                    <span className="summary-value">{summary.retried}</span>
                  </div>
                  <div className="summary-item ignored">
                    <span className="summary-label">○ Ignored:</span>
                    <span className="summary-value">{summary.ignored}</span>
                  </div>
                </div>
                {summary.duplicate_breakdown && (
                  <div className="duplicate-breakdown">
                    <small>Duplicates: {summary.duplicate_breakdown.completed} completed, {summary.duplicate_breakdown.failed} failed, {summary.duplicate_breakdown.pending} pending, {summary.duplicate_breakdown.downloading} downloading</small>
                  </div>
                )}
              </div>
            )}

            {results.length > 0 && (
              <div className="import-results">
                <h3>Detailed Results:</h3>
                <div className="results-list">
                  {results.map((result, index) => (
                    <div key={index} className={`result-item ${result.status}`}>
                      <span className="result-status">
                        {result.status === 'added' && '✓ Added'}
                        {result.status === 'skipped' && '⊘ Skipped'}
                        {result.status === 'retrying' && '↻ Retrying'}
                        {result.status === 'ignored' && '○ Ignored'}
                        {result.status === 'failed' && '✗ Failed'}
                      </span>
                      <span className="result-title">
                        {result.song?.title || 'Unknown'}
                      </span>
                      {result.message && (
                        <span className="result-message">
                          {result.message}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Close
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || !urls.trim()}
              >
                {loading ? 'Importing...' : 'Import URLs'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ImportModal;